import { MAP_SCALE } from "../../../constants.js";
import { TILE } from "../../../constants.js";
import { WORLD_STATE, saveWorldStateToStorage } from "../../../data/worldState.js";
import { ITEM_PICKUP_TUNING } from "../../../data/tuning.js";
import { MAP_DEFS } from "../../../data/maps.js";
import { ItemPickup } from "../../../entities/ItemPickup.js";

function isSpawnInWorldBounds(pos, worldW, worldH, entityW = 28, entityH = 40, margin = 15, bottomTolerance = 8) {
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return false;
  if (pos.x < margin) return false;
  if (pos.x + entityW > worldW - margin) return false;
  if (pos.y < 0) return false;
  if (pos.y + entityH > worldH + bottomTolerance) return false;
  return true;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function getObjectProperty(obj, name) {
  return obj?.properties?.find(p => p.name === name)?.value;
}

export function spawnCollectible(scene, textureKey, x, y) {
  const healAmount = ITEM_PICKUP_TUNING.healByTextureKey?.[textureKey] ?? 5;
  const pickup = new ItemPickup(scene, x, y, textureKey, healAmount);
  pickup.create();
  scene.items.push(pickup);
}

export function spawnRandomTestCollectibles(scene, worldW, worldH, count, textureKeys) {
  const keys = textureKeys || ["collectible_apple", "collectible_heart", "collectible_meat"];
  let spawned = 0;
  let attempts = 0;
  const maxAttempts = count * 12;
  const minX = 120;
  const maxX = Math.max(minX + 1, worldW - 120);

  while (spawned < count && attempts < maxAttempts) {
    attempts++;
    const x = minX + Math.random() * (maxX - minX);
    const ground = scene.findGroundY(x, 0, worldH, null);
    if (!ground) continue;

    const tooClose = scene.items.some((item) => Math.abs(item.baseX - x) < 70 && Math.abs(item.baseY - ground.y) < 28);
    if (tooClose) continue;

    const idx = Math.floor(Math.random() * keys.length);
    spawnCollectible(scene, keys[idx], x, ground.y);
    spawned++;
  }
}

export function resolveSpawnPosition(scene, mapData, targetSpawnName) {
  const worldW = mapData.width * mapData.tilewidth * MAP_SCALE;
  const worldH = mapData.height * mapData.tileheight * MAP_SCALE;
  scene.spawnUsedFallbackFromInvalidResume = false;

  const findNamedSpawn = (name) => {
    if (!name) return null;
    for (const layer of mapData.layers) {
      if (layer.type !== "objectgroup") continue;
      const found = layer.objects?.find((o) => o.name === name);
      if (found) return { x: found.x * MAP_SCALE, y: found.y * MAP_SCALE };
    }
    return null;
  };

  const targetSpawnPos = findNamedSpawn(targetSpawnName);
  if (targetSpawnPos && isSpawnInWorldBounds(targetSpawnPos, worldW, worldH)) {
    return targetSpawnPos;
  }

  if (WORLD_STATE.spawnOverride &&
      typeof WORLD_STATE.spawnOverride.x === "number" &&
      typeof WORLD_STATE.spawnOverride.y === "number") {
    const pos = { x: WORLD_STATE.spawnOverride.x, y: WORLD_STATE.spawnOverride.y };
    WORLD_STATE.spawnOverride = null;
    if (isSpawnInWorldBounds(pos, worldW, worldH)) {
      return pos;
    }
    scene.spawnUsedFallbackFromInvalidResume = true;
  }

  if (WORLD_STATE.resumeEnabled &&
      WORLD_STATE.lastStanding &&
      WORLD_STATE.lastStanding.mapId === scene.mapId &&
      typeof WORLD_STATE.lastStanding.x === "number" &&
      typeof WORLD_STATE.lastStanding.y === "number") {
    const pos = { x: WORLD_STATE.lastStanding.x, y: WORLD_STATE.lastStanding.y };
    if (isSpawnInWorldBounds(pos, worldW, worldH)) {
      return pos;
    }
    WORLD_STATE.lastStanding = null;
    scene.spawnUsedFallbackFromInvalidResume = true;
  }

  const defaultSpawnPos = findNamedSpawn("spawn");
  if (defaultSpawnPos && isSpawnInWorldBounds(defaultSpawnPos, worldW, worldH)) {
    return defaultSpawnPos;
  }

  const hardcodedFallback = { x: 3 * TILE * MAP_SCALE, y: 30 * TILE * MAP_SCALE };
  if (isSpawnInWorldBounds(hardcodedFallback, worldW, worldH, 28, 40, 0)) {
    return hardcodedFallback;
  }

  return {
    x: clamp(hardcodedFallback.x, 0, Math.max(0, worldW - 28)),
    y: clamp(hardcodedFallback.y, 0, Math.max(0, worldH - 40)),
  };
}

export function parsePortalZones(mapData) {
  const portals = [];
  const gameplayLayer = mapData.layers.find((l) => l.type === "objectgroup" && l.name.startsWith("Gameplay"));
  if (!gameplayLayer) return portals;

  for (const obj of gameplayLayer.objects) {
    const objType = String(getObjectProperty(obj, "type") || obj.type || "").toLowerCase();
    if (objType !== "portal") continue;
    const targetMapId = getObjectProperty(obj, "targetMapId");
    if (!targetMapId || !MAP_DEFS[targetMapId]) continue;
    const targetSpawnName = getObjectProperty(obj, "targetSpawnName") || "spawn";
    portals.push({
      x: obj.x * MAP_SCALE,
      y: obj.y * MAP_SCALE,
      w: obj.width * MAP_SCALE,
      h: obj.height * MAP_SCALE,
      targetMapId,
      targetSpawnName,
    });
  }
  return portals;
}

export function tryUsePortal(scene, player, keys, { aabbOverlap }) {
  if (!keys.up || scene.portalCooldownMs > 0) return false;
  const pb = player.getBounds();
  for (const portal of scene.portalZones) {
    if (!aabbOverlap(pb, portal)) continue;
    const target = MAP_DEFS[portal.targetMapId];
    if (!target) continue;
    WORLD_STATE.hp = player.hp;
    WORLD_STATE.lastMapId = scene.mapId;
    WORLD_STATE.lastStanding = { mapId: scene.mapId, x: player.x, y: player.y };
    WORLD_STATE.lastStandingSavedAtMs = scene.time.now;
    WORLD_STATE.spawnOverride = null;
    saveWorldStateToStorage();
    scene.portalCooldownMs = 420;
    scene.scene.start(target.sceneKey, {
      fromMapId: scene.mapId,
      targetSpawnName: portal.targetSpawnName,
    });
    return true;
  }
  return false;
}
